import axios from "axios";
import { triggerSessionAlert } from "../utils/SessionAlertHelper";
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// ─── Shared error handler ────────────────────────────────────────────────────
const handleApiError = (error) => {
  if (!error.response) {
    window.location.href = "/server-down";
    return {
      success: false,
      message:
        "Cannot connect to the server. Please check your network or try again later.",
      error: {
        code: "NETWORK_ERROR",
        message: error.message || "Network Error",
        solution: "Ensure the backend server is running and reachable."
      }
    };
  }

  if (error.response.status === 401) {
    triggerSessionAlert("Your session has expired. Please sign in again.");
    return {
      success: false,
      message: "Your session has expired. Please sign in again.",
      error: {
        code: 401,
        message: "Unauthorized",
        solution: "Please sign in again to continue."
      }
    };
  }

  if (error.response?.status === 403) {
      triggerSessionAlert("Sorry, you do not have permission.");
      return{
        success: false,
        message: "Sorry, you do not have permission.",
        error: {
          code: 403,
          message: "Forbidden",
          solution: "You do not have the necessary permissions to perform this action."
        }
      };
    }

  if (error.response.status >= 500) {
    window.location.href = "/server-down";
    return {
      success: false,
      message: "A server error occurred. Please try again later.",
      error: {
        code: error.response.status,
        message: error.response?.data?.message || "Internal Server Error",
        solution: "Please try again later."
      }
    };
  }

  return {
    success: false,
    message:
      error.response?.data?.message ||
      "An error occurred while fetching dashboard data.",
    error: error.response?.data?.error || {
      code: error.response?.status || 500,
      message: error.message || "Internal Server Error",
      solution: "Please try again later."
    }
  };
};

// ─── Stats API ───────────────────────────────────────────────────────────────
export const getDashboardStats = async (date) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/dashboard/total`, {
      params: { date },
      withCredentials: true
    });

    if (response.data.success) {
      return { success: true, data: response.data.data.dashboardData };
    }

    return { success: false, message: "Failed to fetch stats", error: null };
  } catch (error) {
    return handleApiError(error);
  }
};

// ─── Sales Income API ────────────────────────────────────────────────────────
export const getDashboardSalesIncome = async (date) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/dashboard/sales-income`, {
      params: { date },
      withCredentials: true
    });

    if (response.data.success) {
      return {
        success: true,
        data: response.data.data.dashboardSalesIncomeData
      };
    }

    return {
      success: false,
      message: "Failed to fetch sales income",
      error: null
    };
  } catch (error) {
    return handleApiError(error);
  }
};

// ─── Onboarded Devices API ───────────────────────────────────────────────────
export const getDashboardOnboardedDevices = async (date) => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/dashboard/onboarded-devices`,
      {
        params: { date },
        withCredentials: true
      }
    );

    if (response.data.success) {
      return { success: true, data: response.data.data.onBoardedDevices };
    }

    return {
      success: false,
      message: "Failed to fetch onboarded devices",
      error: null
    };
  } catch (error) {
    return handleApiError(error);
  }
};

// ─── Sales Products API ──────────────────────────────────────────────────────
export const getDashboardSalesProducts = async (date, brandId) => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/dashboard/sales-products`,
      {
        params: { date, brandId },
        withCredentials: true
      }
    );

    if (response.data.success) {
      return { success: true, data: response.data.data.salesProducts };
    }

    return {
      success: false,
      message: "Failed to fetch sales products",
      error: null
    };
  } catch (error) {
    return handleApiError(error);
  }
};

// ─── Top Selling Products API ────────────────────────────────────────────────
export const getDashboardTopSellingProducts = async (date, brandId) => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/dashboard/top-selling-products`,
      {
        params: { date, brandId },
        withCredentials: true
      }
    );

    if (response.data.success) {
      return { success: true, data: response.data.data.topSellingProducts };
    }

    return {
      success: false,
      message: "Failed to fetch top selling products",
      error: null
    };
  } catch (error) {
    return handleApiError(error);
  }
};
